// EN/FR copy for the History page. The page renders its own language (data-cvi-i18n="managed"),
// so user-entered history (activity titles, goals, questions) is never passed through the
// page translator.

const plural = (n, one, many) => (n === 1 ? one : many);

const COPY = {
    en: {
        locale: "en-CA",
        title: "Performance History",
        subtitle: {
            personal: "How your CVI, Individual Contribution and Collective Contribution have evolved from one period to the next. Finalized periods are read-only.",
            organization: "How the organization’s results have evolved from one period to the next. Finalized periods are read-only.",
        },
        views: { label: "History view", period: "By period", person: "By person" },
        focusLabel: "Period",
        status: { final: "Finalized", provisional: "Provisional", unknown: "Not available" },
        statusHelp: { final: "Final snapshot · read-only", provisional: "Active period · values change until it is locked" },
        overview: {
            title: { personal: "Results for the selected period", organization: "Organization results for the selected period" },
            cvi: "CVI",
            individual: "Individual Contribution",
            collective: "Collective Contribution",
            outOf: "/ 100",
            vs: label => `vs ${label}`,
            noPrevious: "No earlier finalized period to compare with",
            withheld: "Compared once this period is finalized",
            notAvailable: "Not available",
            mean: "Average of available finalized results",
            noScore: "No result was recorded for this period.",
        },
        trend: {
            title: "Performance trend",
            subtitle: "CVI, Individual Contribution and Collective Contribution by period. Gaps mean no result was recorded.",
            cvi: "CVI", individual: "Individual Contribution", collective: "Collective Contribution",
            provisional: "Provisional", final: "Finalized",
            noData: "No result",
            empty: "The trend appears once at least one period has a result.",
            tableCaption: "Results by period",
            selectHint: "Select a period to see its results.",
        },
        table: {
            title: "Periods",
            subtitle: "Most recent first. Select a period to see it above, or open its full record.",
            period: "Period", status: "Status", cvi: "CVI", individual: "Individual", collective: "Collective",
            change: "Change", actions: "Actions",
            details: "View details", summary: "Summary",
            count: n => `${n} ${plural(n, "period", "periods")}`,
            year: "Year", quarter: "Quarter", allYears: "All years", allQuarters: "All quarters", clear: "Clear filters",
            empty: "No periods have results yet. Results appear here once a period has been evaluated.",
            noMatch: "No period matches the selected filters.",
            withheld: "After finalization",
        },
        person: {
            title: "History by person",
            subtitle: "Select a person you are authorized to see to review all of their periods.",
            position: "Position", employee: "Person", allRoles: "All positions",
            loading: "Loading people…", empty: "No person matches the selected filters.",
            periods: n => `${n} ${plural(n, "period", "periods")}`,
        },
        detail: {
            back: "Back to History",
            previous: "Previous period", next: "Next period",
            readOnly: "Finalized results · read-only. Values come from the final snapshot stored when the period was locked and are never recalculated with a newer formula.",
            provisional: "Provisional results. This period is still open; values come from the live CVI calculation and will change until the period is locked.",
            person: "Person", position: "Position", allRoles: "All positions",
            loading: "Loading the period record…",
            error: "This period’s record could not be loaded.",
            retry: "Try again",
            noSnapshot: "No result is available for this person in this period.",
            sections: {
                routine: "Core Responsibilities",
                routineManager: "Manager evaluation",
                routineSelf: "Self-assessment",
                activities: "Additional Contributions",
                goals: "Individual Goals",
                peer: "Peer Feedback",
                collective: "Collective Contribution",
                calculation: "Calculation details",
            },
            criterion: "Criterion", rating: "Rating", month: m => `Month ${m}`,
            noneRecorded: "Nothing was recorded for this section in this period.",
            notInSnapshot: "Component details were not stored with this period’s final result.",
            peerDisabled: "Peer feedback was not part of this person’s evaluation.",
            responses: n => `${n} valid ${plural(n, "response", "responses")}`,
            weight: "Weight", target: "Target", actual: "Actual", achievement: "Achievement",
            managerScore: "Manager’s final score", evidence: "Evidence", managerComment: "Manager comment",
            points: n => `${n} ${plural(n, "point", "points")}`,
            levels: { team: "Team", department: "Parent units", organization: "Organization" },
            levelSummary: (weight, result) => `Weight ${weight} · Result ${result}`,
            indicator: "Indicator",
            formula: "Formula version",
            source: "Source",
            sourceFinal: version => `Final snapshot${version ? ` · version ${version}` : ""}`,
            sourceLive: "Live calculation (provisional)",
            calculatedAt: "Calculated",
            untitled: "Untitled contribution",
        },
        statuses: {
            draft: "Draft", submitted: "Submitted", pending_review: "Pending review", resubmitted: "Resubmitted",
            revision_requested: "Revision required", approved: "Approved", rejected: "Rejected", locked: "Locked",
            active: "Active", proposed: "Proposed", completed: "Completed", cancelled: "Cancelled",
        },
        levels: {},
    },
    fr: {
        locale: "fr-CA",
        title: "Historique des résultats",
        subtitle: {
            personal: "Évolution de votre CVI, de votre contribution individuelle et de votre contribution collective d’une période à l’autre. Les périodes finalisées sont en lecture seule.",
            organization: "Évolution des résultats de l’organisation d’une période à l’autre. Les périodes finalisées sont en lecture seule.",
        },
        views: { label: "Vue de l’historique", period: "Par période", person: "Par personne" },
        focusLabel: "Période",
        status: { final: "Finalisée", provisional: "Provisoire", unknown: "Non disponible" },
        statusHelp: { final: "Instantané final · lecture seule", provisional: "Période active · les valeurs changent jusqu’au verrouillage" },
        overview: {
            title: { personal: "Résultats de la période sélectionnée", organization: "Résultats de l’organisation pour la période sélectionnée" },
            cvi: "CVI",
            individual: "Contribution individuelle",
            collective: "Contribution collective",
            outOf: "/ 100",
            vs: label => `par rapport à ${label}`,
            noPrevious: "Aucune période finalisée antérieure à comparer",
            withheld: "Comparaison à la finalisation de la période",
            notAvailable: "Non disponible",
            mean: "Moyenne des résultats finalisés disponibles",
            noScore: "Aucun résultat n’a été enregistré pour cette période.",
        },
        trend: {
            title: "Évolution des résultats",
            subtitle: "CVI, contribution individuelle et contribution collective par période. Une interruption signifie qu’aucun résultat n’a été enregistré.",
            cvi: "CVI", individual: "Contribution individuelle", collective: "Contribution collective",
            provisional: "Provisoire", final: "Finalisée",
            noData: "Aucun résultat",
            empty: "L’évolution s’affiche dès qu’au moins une période a un résultat.",
            tableCaption: "Résultats par période",
            selectHint: "Sélectionnez une période pour voir ses résultats.",
        },
        table: {
            title: "Périodes",
            subtitle: "De la plus récente à la plus ancienne. Sélectionnez une période pour l’afficher ci-dessus ou ouvrez son dossier complet.",
            period: "Période", status: "Statut", cvi: "CVI", individual: "Individuelle", collective: "Collective",
            change: "Variation", actions: "Actions",
            details: "Voir les détails", summary: "Résumé",
            count: n => `${n} ${plural(n, "période", "périodes")}`,
            year: "Année", quarter: "Trimestre", allYears: "Toutes les années", allQuarters: "Tous les trimestres", clear: "Effacer les filtres",
            empty: "Aucune période n’a encore de résultat. Les résultats s’affichent ici dès qu’une période a été évaluée.",
            noMatch: "Aucune période ne correspond aux filtres sélectionnés.",
            withheld: "À la finalisation",
        },
        person: {
            title: "Historique par personne",
            subtitle: "Sélectionnez une personne dont vous pouvez consulter les résultats pour voir toutes ses périodes.",
            position: "Poste", employee: "Personne", allRoles: "Tous les postes",
            loading: "Chargement des personnes…", empty: "Aucune personne ne correspond aux filtres sélectionnés.",
            periods: n => `${n} ${plural(n, "période", "périodes")}`,
        },
        detail: {
            back: "Retour à l’historique",
            previous: "Période précédente", next: "Période suivante",
            readOnly: "Résultats finalisés · lecture seule. Les valeurs proviennent de l’instantané final enregistré au verrouillage de la période et ne sont jamais recalculées avec une nouvelle formule.",
            provisional: "Résultats provisoires. Cette période est encore ouverte; les valeurs proviennent du calcul CVI en direct et changeront jusqu’au verrouillage de la période.",
            person: "Personne", position: "Poste", allRoles: "Tous les postes",
            loading: "Chargement du dossier de la période…",
            error: "Le dossier de cette période n’a pas pu être chargé.",
            retry: "Réessayer",
            noSnapshot: "Aucun résultat n’est disponible pour cette personne pour cette période.",
            sections: {
                routine: "Responsabilités principales",
                routineManager: "Évaluation du gestionnaire",
                routineSelf: "Autoévaluation",
                activities: "Contributions additionnelles",
                goals: "Objectifs individuels",
                peer: "Rétroaction des collègues",
                collective: "Contribution collective",
                calculation: "Détails du calcul",
            },
            criterion: "Critère", rating: "Évaluation", month: m => `Mois ${m}`,
            noneRecorded: "Rien n’a été enregistré pour cette section pendant cette période.",
            notInSnapshot: "Le détail des composantes n’a pas été conservé avec le résultat final de cette période.",
            peerDisabled: "La rétroaction des collègues ne faisait pas partie de l’évaluation de cette personne.",
            responses: n => `${n} ${plural(n, "réponse valide", "réponses valides")}`,
            weight: "Pondération", target: "Cible", actual: "Réel", achievement: "Atteinte",
            managerScore: "Résultat final attribué par le gestionnaire", evidence: "Preuve", managerComment: "Commentaire du gestionnaire",
            points: n => `${n} ${plural(n, "point", "points")}`,
            levels: { team: "Équipe", department: "Unités parentes", organization: "Organisation" },
            levelSummary: (weight, result) => `Pondération ${weight} · Résultat ${result}`,
            indicator: "Indicateur",
            formula: "Version de la formule",
            source: "Source",
            sourceFinal: version => `Instantané final${version ? ` · version ${version}` : ""}`,
            sourceLive: "Calcul en direct (provisoire)",
            calculatedAt: "Calculé le",
            untitled: "Contribution sans titre",
        },
        statuses: {
            draft: "Brouillon", submitted: "Soumis", pending_review: "En attente de révision", resubmitted: "Soumis de nouveau",
            revision_requested: "Révision requise", approved: "Approuvé", rejected: "Refusé", locked: "Verrouillé",
            active: "Actif", proposed: "Proposé", completed: "Terminé", cancelled: "Annulé",
        },
        // Default rating levels, worded as elsewhere in the app; custom level names are shown as entered.
        levels: { "Excellent": "Excellent", "Good": "Bon", "Meets expectations": "Répond aux attentes", "Needs improvement": "À améliorer", "Unsatisfactory": "Insatisfaisant" },
    },
};

export function historyCopy(language) {
    return COPY[language === "fr" ? "fr" : "en"];
}
